import gql from 'graphql-tag';
import {pick} from 'lodash';
import {useCallback, useMemo, useRef} from 'react';
import {createSelector, type Selector} from 'reselect';

import {useSingleRunModeContext} from '../../components/MultiRunWorkspace/SingleRunMode';
import {usePinnedRuns} from '../../components/RunSelector/RunSelectorRunsContext';
import {
  makeRunSetGroupSelectionsSelector,
  makeRunSetSelector,
  makeSingleRunGroupSelectionsSelector,
} from '../../components/RunSelector/runSetSelectors';
import * as Generated from '../../generated/graphql';
import {type RootState} from '../../types/redux';
import {useCachedData} from '../../util/cache';
import {propagateErrorsContext} from '../../util/errors';
import * as Filters from '../../util/filters';
import type * as FilterTypes from '../../util/filterTypes';
import {getFiltersForQuery, type StoredFilter} from '../../util/filterV2';
import {excludedRunIdsFilter} from '../../util/hooks/usePinnedRunsFilters';
import type * as RunTypes from '../../util/runTypes';
import {type RunSetConfig} from '../../util/section';
import * as SM from '../../util/selectionmanager';
import {useMemoizedSelector} from '../hooks';
import {useSyncedPollingTick} from '../polling/hooks';
import {useRunsQueryContext} from '../runs/hooks';
import type * as CustomRunNamesTypes from '../views/customRunNames/types';
import {type Ref as FilterRef} from '../views/filter/types';
import * as ViewHooks from '../views/hooks';
import {useRunSetProject} from '../views/runSet/hooks';
import type * as RunSetTypes from '../views/runSet/types';
import {type Ref as RunSetRef, type RunSetPart} from '../views/runSet/types';
import type * as TempSelectionsTypes from '../views/tempSelections/types';
import {combinedFilter} from './runSelectorQuery';

/**
 * Uses createSelector per https://reselect.js.org/api/createSelector
 * Colocated here since it's specific to useSelectedCountsWithRefetch.
 */
const makeRunSetCountQueryPropsSelector = (
  runSetRef: RunSetRef
): Selector<
  RootState,
  {
    filtersRef: FilterRef;
    project: RunSetPart['project'];
    search: RunSetPart['search'];
  }
> =>
  createSelector(makeRunSetSelector(runSetRef), runSet => ({
    filtersRef: runSet.filtersRef as FilterRef,
    project: runSet.project,
    search: runSet.search,
  }));

///// Query definition
export const SELECTED_COUNT_QUERY = gql`
  query SelectedCount(
    $projectInternalId: ID!
    $filters: JSONString
    $tempFilters: JSONString
  ) {
    project(internalId: $projectInternalId) {
      id
      selectedCount: runCount(filters: $filters)
      tempSelectedCount: runCount(filters: $tempFilters)
      tempSelectedActiveCount: runActiveCount(filters: $tempFilters)
      tempOutputArtifactsCount: runOutputArtifactsCount(filters: $tempFilters)
    }
  }
`;

interface QueryInputProps {
  mergeFilters?: FilterTypes.Filter<RunTypes.Key>;
  spamFilters?: FilterTypes.Filter<RunTypes.Key>;
  filters: StoredFilter;
  groupSelection: SM.GroupSelectionState;
  groupTempSelection?: SM.GroupSelectionState;
  search?: RunSetConfig['search'];
  customRunNames?: CustomRunNamesTypes.CustomRunNames;
  projectInternalId: string | undefined;
  pinnedRunIds: string[];
  countAllListedRuns?: boolean;
  onlyShowSelected?: boolean;
  skip?: boolean;
}

/**
 * Selects the group-selection filter for the primary count query.
 *
 * Visibility-aware counts keep the real selection state so hidden/eyeball-off
 * runs stay excluded. Listed counts also keep it when "only show selected" is
 * active. When counting every listed row, clear only the selections so the count
 * matches the table rows while preserving grouping/search behavior.
 */
export function getSelectedCountGroupSelection({
  groupSelection,
  countAllListedRuns,
  onlyShowSelected,
}: {
  groupSelection: SM.GroupSelectionState;
  countAllListedRuns?: boolean;
  onlyShowSelected?: boolean;
}): SM.GroupSelectionState {
  if (!countAllListedRuns || onlyShowSelected) {
    return groupSelection;
  }
  return {
    ...groupSelection,
    selections: SM.EMPTY_ALL_SELECTIONS,
  };
}

/**
 * Group selection for the listed count's pinned-run tally. Pinned rows always
 * render regardless of "only show selected", so count every pin when counting
 * every listed row.
 */
export function getPinnedCountGroupSelection({
  groupSelection,
  countAllListedRuns,
}: {
  groupSelection: SM.GroupSelectionState;
  countAllListedRuns?: boolean;
}): SM.GroupSelectionState {
  if (!countAllListedRuns) {
    return groupSelection;
  }
  return {
    ...groupSelection,
    selections: SM.EMPTY_ALL_SELECTIONS,
  };
}

export function useSelectedCountQuery(
  inputProps: QueryInputProps
): ReturnType<typeof useSelectedCountQueryNoCache> {
  const selectedCountQuery = useSelectedCountQueryNoCache(inputProps);

  const initialLoading =
    selectedCountQuery.loading && selectedCountQuery.data == null;

  const selectedCountFromServerOrCache = useCachedData({
    namespace: 'SelectedCountQuery',
    key: inputProps,
    dataFromServer: selectedCountQuery.data,
    waitingForDataFromServer: initialLoading,
  });

  return useMemo(() => {
    if (initialLoading && selectedCountFromServerOrCache != null) {
      return {
        ...selectedCountQuery,
        data: selectedCountFromServerOrCache,
        loading: false,
      };
    }
    return selectedCountQuery;
  }, [initialLoading, selectedCountFromServerOrCache, selectedCountQuery]);
}

function useSelectedCountQueryNoCache(inputProps: QueryInputProps) {
  // Exclude pinned runs from query so they don't get double-counted.
  // Num pins counted in the RunSelector are computed client-side and added
  // separately.
  const mergeFiltersWithoutPins = useMemo(() => {
    const exclusion = excludedRunIdsFilter(inputProps.pinnedRunIds);
    if (exclusion == null) {
      return inputProps.mergeFilters;
    }
    if (inputProps.mergeFilters == null) {
      return exclusion;
    }
    return Filters.And([inputProps.mergeFilters, exclusion]);
  }, [inputProps.mergeFilters, inputProps.pinnedRunIds]);

  const queryFilters = useMemo(
    () => getFiltersForQuery(inputProps.filters),
    [inputProps.filters]
  );

  const primaryCountGroupSelection = useMemo(
    () =>
      getSelectedCountGroupSelection({
        groupSelection: inputProps.groupSelection,
        onlyShowSelected: inputProps.onlyShowSelected,
        countAllListedRuns: inputProps.countAllListedRuns,
      }),
    [
      inputProps.groupSelection,
      inputProps.onlyShowSelected,
      inputProps.countAllListedRuns,
    ]
  );

  return Generated.useSelectedCountQuery({
    variables: {
      projectInternalId: inputProps.projectInternalId ?? '',
      filters: JSON.stringify(
        combinedFilter(
          queryFilters,
          primaryCountGroupSelection,
          inputProps.spamFilters,
          mergeFiltersWithoutPins,
          inputProps.search,
          inputProps.customRunNames
        )
      ),
      tempFilters: JSON.stringify(
        combinedFilter(
          queryFilters,
          inputProps.groupTempSelection,
          undefined,
          mergeFiltersWithoutPins,
          inputProps.search,
          inputProps.customRunNames
        )
      ),
    },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    context: propagateErrorsContext(),
    skip: Boolean(inputProps.skip) || inputProps.projectInternalId == null,
  });
}

/**
 * Selector factory: derive the selection state for a run set.
 *
 * Uses makeSingleRunGroupSelectionsSelector so the single-run mode override
 * is handled upstream — this selector only concerns itself with pin
 * augmentation.
 */
const selectRunSelections = (
  runSetRef: RunSetRef,
  singleRunName?: string
): Selector<RootState, SM.GroupSelectionState> =>
  createSelector(
    [
      singleRunName
        ? makeSingleRunGroupSelectionsSelector(runSetRef)
        : makeRunSetGroupSelectionsSelector(runSetRef),
    ],
    groupSelections => {
      return {
        selections: groupSelections.selections,
        grouping: groupSelections.grouping,
        expandedRowAddresses: groupSelections.expandedRowAddresses,
      };
    }
  );

function useSelections(runSetRef: RunSetTypes.Ref): SM.GroupSelectionState {
  const {runName} = useSingleRunModeContext();
  return useMemoizedSelector(selectRunSelections, runSetRef, runName);
}

type Counts = {
  tempOutputArtifactsCount: number;
  tempSelectedCount: number;
  tempSelectedActiveCount: number;
  selectedCount: number;
};

const zeroCounts: Counts = {
  tempOutputArtifactsCount: 0,
  tempSelectedCount: 0,
  tempSelectedActiveCount: 0,
  selectedCount: 0,
};

/**
 * Count how many pinned runs should be included based on the same
 * selection/visibility state used for the primary selected count. We
 * compute this client-side because SelectedCount
 * query is project-scoped, but pinned runs may be cross-project.
 * This count will be added to SelectedCount results to get the final count.
 */
export function countPinnedRunsForSelectedCounts(
  pinnedRuns: ReturnType<typeof usePinnedRuns>,
  groupSelection: SM.GroupSelectionState,
  groupTempSelection?: SM.GroupSelectionState
): Pick<Counts, 'selectedCount' | 'tempSelectedCount'> {
  if (!pinnedRuns || pinnedRuns.length === 0) {
    return {selectedCount: 0, tempSelectedCount: 0};
  }
  const selectionFilter = SM.toRunFilter(groupSelection);
  const tempSelectionFilter = groupTempSelection
    ? SM.toRunFilter(groupTempSelection)
    : null;

  let selectedCount = 0;
  let tempSelectedCount = 0;
  for (const pinned of pinnedRuns) {
    if (Filters.match(selectionFilter, pinned)) {
      selectedCount++;
    }
    if (
      tempSelectionFilter != null &&
      Filters.match(tempSelectionFilter, pinned)
    ) {
      tempSelectedCount++;
    }
  }
  return {selectedCount, tempSelectedCount};
}

/**
 * Get selected counts for a run set. Returns a tuple of two items, the second
 * is a function to refetch the counts. If counts are not loaded, all properties
 * will be zero.
 *
 * @param runSetRef
 * @param tempSelectionsRef
 * @param customRunNamesRef
 * @return Tuple of counts and refetch function.
 */
export function useSelectedCountsWithRefetch(
  runSetRef: RunSetTypes.Ref,
  tempSelectionsRef?: TempSelectionsTypes.Ref,
  customRunNamesRef?: CustomRunNamesTypes.Ref,
  options: {
    countAllListedRuns?: boolean;
    onlyShowSelected?: boolean;
    skip?: boolean;
    pollOnTick?: boolean;
  } = {}
): readonly [Counts, () => void] {
  const lastCountsRef = useRef<Counts>(zeroCounts);

  const customRunNames = ViewHooks.usePartMaybe(customRunNamesRef);
  const tempSelections = ViewHooks.usePartMaybe(tempSelectionsRef);
  const pinnedRuns = usePinnedRuns();
  const pinnedRunIds = useMemo(
    () => pinnedRuns?.map(r => r.id) ?? [],
    [pinnedRuns]
  );

  // Use useMemoizedSelector with reselect selector factory.
  // See components/RunSelector/runSetSelectors.ts for available selectors.
  const partialRunSet = useMemoizedSelector(
    makeRunSetCountQueryPropsSelector,
    runSetRef
  );

  const filters = ViewHooks.usePart(partialRunSet.filtersRef);

  const runsQueryContext = useRunsQueryContext();
  const {projectInternalId} = useRunSetProject(partialRunSet.project);
  const groupSelections = useSelections(runSetRef);

  const groupTempSelection = useMemo(
    () =>
      tempSelections
        ? {
            grouping: groupSelections.grouping,
            selections: tempSelections,
            expandedRowAddresses: groupSelections.expandedRowAddresses,
          }
        : undefined,
    [tempSelections, groupSelections]
  );

  // Pins always render, so count them all when counting every listed row. The server count
  // below still honors onlyShowSelected for non-pinned rows.
  const pinnedCountGroupSelection = useMemo(
    () =>
      getPinnedCountGroupSelection({
        groupSelection: groupSelections,
        countAllListedRuns: options.countAllListedRuns,
      }),
    [groupSelections, options.countAllListedRuns]
  );

  const selectedCountQueryProps = {
    ...runsQueryContext,
    projectInternalId,
    filters,
    groupSelection: groupSelections,
    groupTempSelection,
    search: partialRunSet.search,
    customRunNames,
    pinnedRunIds,
    countAllListedRuns: options.countAllListedRuns,
    onlyShowSelected: options.onlyShowSelected,
    skip: options.skip,
  };
  const selectedCountQuery = useSelectedCountQuery(selectedCountQueryProps);

  const refetchCounts = useCallback(() => {
    selectedCountQuery.refetch();
  }, [selectedCountQuery]);

  useSyncedPollingTick('SelectedCount', () => {
    if (
      options.pollOnTick &&
      projectInternalId != null &&
      !selectedCountQuery.loading
    ) {
      refetchCounts();
    }
  });

  const counts = useMemo(() => {
    if (selectedCountQuery.loading) {
      return lastCountsRef.current;
    }

    if (!selectedCountQuery.data?.project) {
      // Hold the last known counts instead of dropping to zero on a transient
      // no-project render (e.g. the brief gap while a post-delete or poll
      // refetch settles), so the count tag doesn't flash through "0 listed".
      // A genuinely empty project still returns data.project with a 0 count and
      // flows through the branch below, so this doesn't mask a real zero.
      // lastCountsRef starts at zeroCounts, so the initial load still reads 0.
      return lastCountsRef.current;
    }

    const project = selectedCountQuery.data.project;
    const serverCounts = pick(project, [
      'tempOutputArtifactsCount',
      'tempSelectedCount',
      'tempSelectedActiveCount',
      'selectedCount',
    ]);

    const pinnedCounts = countPinnedRunsForSelectedCounts(
      pinnedRuns,
      pinnedCountGroupSelection,
      groupTempSelection
    );

    const counts = {
      ...serverCounts,
      selectedCount: serverCounts.selectedCount + pinnedCounts.selectedCount,
      tempSelectedCount:
        serverCounts.tempSelectedCount + pinnedCounts.tempSelectedCount,
    };

    lastCountsRef.current = counts;

    return counts;
  }, [
    selectedCountQuery.data?.project,
    selectedCountQuery.loading,
    pinnedRuns,
    pinnedCountGroupSelection,
    groupTempSelection,
  ]);

  return [counts, refetchCounts] as const;
}

/**
 * As with `useSelectedCountsWithRefetch`, but only returns the counts, not the
 * refetch function.
 *
 * @param runSetRef
 * @param tempSelectionsRef
 * @param customRunNamesRef
 * @returns Counts
 */
export function useSelectedCounts(
  runSetRef: RunSetTypes.Ref,
  tempSelectionsRef?: TempSelectionsTypes.Ref,
  customRunNamesRef?: CustomRunNamesTypes.Ref,
  options?: {
    countAllListedRuns?: boolean;
    onlyShowSelected?: boolean;
    skip?: boolean;
    pollOnTick?: boolean;
  }
): Counts {
  const [counts] = useSelectedCountsWithRefetch(
    runSetRef,
    tempSelectionsRef,
    customRunNamesRef,
    options
  );
  return counts;
}
